import React from 'react';
import { AlertTriangle, RefreshCw, RotateCw } from 'lucide-react';

interface ErrorBoundaryProps {
  children: React.ReactNode;
  /**
   * When this value changes, a currently-displayed error is cleared. Pass the
   * route (e.g. location.pathname) so navigating away from a crashed page
   * auto-recovers instead of the error screen persisting across navigation.
   */
  resetKey?: unknown;
  /** Telemetry sink. Receives the caught error + React info. */
  onError?: (error: Error, info: React.ErrorInfo) => void;
  /** Optional label so error reports can name the boundary's scope. */
  label?: string;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // Always log; also forward to an optional telemetry sink so a future
    // error-reporting service can hook in without touching call sites.
    console.error(`ErrorBoundary${this.props.label ? ` [${this.props.label}]` : ''} caught:`, error, info);
    this.props.onError?.(error, info);
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps) {
    // Auto-recover on navigation: when resetKey changes (e.g. the route), drop
    // the error so the destination page renders instead of the stuck error UI.
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false, error: null });
    }
  }

  private handleReset = () => this.setState({ hasError: false, error: null });

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex items-center justify-center h-full bg-wiz-darker" role="alert" aria-live="assertive">
          <div className="text-center max-w-md p-8">
            <div className="w-16 h-16 mx-auto mb-4 bg-red-500/10 rounded-full flex items-center justify-center">
              <AlertTriangle className="w-8 h-8 text-red-400" />
            </div>
            <h2 className="text-xl font-semibold text-white mb-2">Something went wrong</h2>
            <p className="text-gray-400 mb-5 text-sm">
              {this.state.error?.message || 'An unexpected error occurred on this page.'}
            </p>
            <div className="flex items-center gap-2 justify-center">
              <button
                onClick={this.handleReset}
                className="px-4 py-2 bg-wiz-purple hover:brightness-110 text-white rounded-lg flex items-center gap-2 transition-colors"
              >
                <RefreshCw className="w-4 h-4" />
                Try again
              </button>
              <button
                onClick={() => window.location.reload()}
                className="px-4 py-2 bg-wiz-card border border-wiz-border text-gray-300 hover:text-white rounded-lg flex items-center gap-2 transition-colors"
              >
                <RotateCw className="w-4 h-4" />
                Reload page
              </button>
            </div>
            <p className="text-xs text-gray-600 mt-4">
              The rest of the app keeps working — you can navigate elsewhere from the sidebar.
            </p>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
